import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SiteLayout, SEA, SAND, BrandMark } from '../components/SiteLayout.tsx';
import { FlagGlyph } from '../components/SignalFlags.tsx';

/** Maritimt hero. */
function Hero() {
  return (
    <section className="relative overflow-hidden text-slate-100" style={{ background: '#0a2740' }}>
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 620"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="rc-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0a2740" />
            <stop offset="0.55" stopColor="#14466a" />
            <stop offset="0.8" stopColor="#2f6f86" />
            <stop offset="1" stopColor="#d98a4a" />
          </linearGradient>
          <linearGradient id="rc-sea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e7b27f" />
            <stop offset="0.12" stopColor="#2d6c86" />
            <stop offset="1" stopColor="#07233b" />
          </linearGradient>
          <radialGradient id="rc-sun" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#ffd9a8" stopOpacity="0.95" />
            <stop offset="1" stopColor="#ffd9a8" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="1440" height="392" fill="url(#rc-sky)" />
        <circle cx="1050" cy="372" r="150" fill="url(#rc-sun)" />
        <circle cx="1050" cy="384" r="40" fill="#ffcf9b" opacity="0.85" />
        <path
          d="M0 372 q120 -26 240 -8 t240 -4 q130 14 260 -10 t300 2 q140 16 240 6 l0 30 l-1480 0 Z"
          fill="#0b2a42"
          opacity="0.9"
        />
        <rect x="0" y="388" width="1440" height="232" fill="url(#rc-sea)" />
        <g stroke="#7fb4c6" strokeOpacity="0.25" strokeWidth="2" fill="none" strokeLinecap="round">
          <path d="M120 440 q60 -8 120 0" />
          <path d="M360 470 q70 -9 140 0" />
          <path d="M740 452 q60 -8 120 0" />
          <path d="M980 486 q70 -9 140 0" />
          <path d="M520 512 q80 -10 160 0" />
          <path d="M220 556 q80 -10 160 0" />
        </g>
        <g opacity="0.96">
          <path d="M980 388 L980 300 L916 388 Z" fill="#f4f9fb" />
          <path d="M988 388 L988 318 L1040 388 Z" fill="#dfeef3" />
          <line x1="980" y1="300" x2="980" y2="392" stroke="#0a2740" strokeWidth="2" />
        </g>
        <g opacity="0.9">
          <path d="M760 392 L760 332 L716 392 Z" fill="#eaf4f7" />
          <line x1="760" y1="332" x2="760" y2="394" stroke="#0a2740" strokeWidth="1.6" />
        </g>
      </svg>
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(100deg,rgba(7,28,47,.94) 0%,rgba(8,32,54,.80) 45%,rgba(10,39,64,.35) 100%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <span
          className="inline-flex items-center gap-2.5 text-xs font-bold tracking-[0.14em] uppercase"
          style={{ color: SAND }}
        >
          <span className="w-6 h-0.5 rounded" style={{ background: SAND }} />
          Plattform for seilregatta
        </span>
        <h1 className="mt-4 font-extrabold tracking-tight text-white text-4xl sm:text-5xl leading-[1.05] max-w-[17ch]">
          Fra startlinje til målgang — live på kartet
        </h1>
        <p className="mt-4 text-slate-300 max-w-[52ch] text-base sm:text-lg">
          Racecloud samler klubbenes båter, bøyer, baner og regattaer på ett sted. Seilere melder på
          og følger løpet live. Komiteen bekrefter målgangen med GPS.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/regattaer"
            className="px-6 py-3 rounded-xl font-bold text-white hover:brightness-110 transition"
            style={{ background: SAND }}
          >
            ▶ Se regattaer
          </Link>
          <Link
            to="/konto/ny"
            className="px-6 py-3 rounded-xl font-bold text-white hover:brightness-110 transition"
            style={{ background: SEA }}
          >
            Be om konto
          </Link>
        </div>
        <div className="mt-9 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-300">
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: SEA }} />
            <b className="text-white font-semibold">GPS-sporing</b> i sanntid
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: SEA }} />
            <b className="text-white font-semibold">Kartverket</b>-sjøkart
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: SEA }} />
            <b className="text-white font-semibold">Flere klubber</b>, felles plattform
          </span>
        </div>
      </div>
    </section>
  );
}

/** Det appen gjør om bord — én rute per funksjon. */
const APP_FUNKSJONER: { tittel: string; tekst: string; ikon: ReactNode }[] = [
  {
    tittel: 'Startklokke',
    tekst:
      'Femminutters startprosedyre med flagg, lyd og vibrasjon. Har regattaen fast starttid, går klokka i takt med serveren — ellers starter båten klokka selv.',
    ikon: (
      <>
        <circle cx="12" cy="13.5" r="7.5" />
        <path d="M12 13.5V10M9.5 3h5M12 3v3M18.5 7l1.5-1.5" />
      </>
    ),
  },
  {
    tittel: 'Startlinje',
    tekst:
      'Ping linjeendene — babord og styrbord — eller hent linja fra banen. Du ser avstand og tid til linja, og «burn»: hvor mange sekunder du er for tidlig eller for sent.',
    ikon: (
      <>
        <circle cx="4.5" cy="6.5" r="2" />
        <circle cx="19.5" cy="6.5" r="2" />
        <path d="M7.5 6.5h9" strokeDasharray="2 2.2" />
        <path d="M12 21v-9.5M8.8 14.7 12 11.5l3.2 3.2" />
      </>
    ),
  },
  {
    tittel: 'Instrument',
    tekst: 'Fart i knop eller km/t, kurs med kompass og posisjon — store tall som er lette å lese om bord.',
    ikon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2.3 4.7-4.7 2.3 2.3-4.7z" />
      </>
    ),
  },
  {
    tittel: 'Live-sporing',
    tekst:
      'Når båten seiler regatta, sender telefonen posisjonen hvert tredje sekund. Publikum og komiteen følger båten på live-kartet, og etterpå kan løpet spilles av.',
    ikon: (
      <>
        <path d="M12 21s-6.5-5.6-6.5-10.5a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21z" />
        <circle cx="12" cy="10.5" r="2.3" />
      </>
    ),
  },
  {
    tittel: 'Trening og tur',
    tekst:
      'Logg treningen som en egen aktivitet. Uten dekning lagres sporet på telefonen og sendes når nettet er tilbake. Etterpå ligger turen på Min side med kart og reprise.',
    ikon: (
      <>
        <circle cx="5" cy="18.5" r="2" />
        <circle cx="19" cy="5.5" r="2" />
        <path d="M7 18.5h7.5a3.25 3.25 0 0 0 0-6.5h-5a3.25 3.25 0 0 1 0-6.5H17" />
      </>
    ),
  },
  {
    tittel: 'Komité',
    tekst:
      'Ved komitéstyrt start styrer komiteen klokka fra sin telefon: start, utsettelse med AP eller nullstilling. Alle båtene følger den samme klokka.',
    ikon: (
      <>
        <path d="M5.5 21V3.5" />
        <path d="M5.5 4h12l-2.5 4 2.5 4h-12" />
      </>
    ),
  },
];

/** Appens farger (app/src/screens) — telefonbildet skal ligne den ekte appen. */
const APP = { bg: '#0a2740', kort: '#0c2942', fane: '#0e2f4b', kant: '#1b4a6b', dempet: '#9fb8cb', svak: '#6b8196' };

/** Stilisert telefon med appens startklokke ett minutt før start: 48 s igjen, 31 s til linja = burn +17 s. */
function PhoneMock() {
  const avlesninger = [
    { label: 'Avstand til linja', verdi: '62 m', stor: true },
    { label: 'Burn', verdi: '+17 s', hint: 'for tidlig — brenn tid' },
    { label: 'Tid til linja', verdi: '0:31' },
    { label: 'Linjelengde', verdi: '120 m' },
  ];
  return (
    <figure className="mx-auto w-[272px]">
      <div
        aria-hidden="true"
        className="rounded-[2.6rem] p-2 bg-slate-800 ring-1 ring-slate-600/70 shadow-2xl shadow-black/50"
      >
        <div className="relative rounded-[2.1rem] overflow-hidden text-white" style={{ background: APP.bg }}>
          {/* Statuslinje */}
          <div className="flex items-center justify-between px-6 h-8 text-[11px] font-semibold">
            <span>18:29</span>
            <span className="absolute left-1/2 top-2.5 -translate-x-1/2 w-3 h-3 rounded-full bg-black" />
            <span className="flex items-center gap-1.5">
              <svg width="15" height="10" viewBox="0 0 15 10" fill="currentColor">
                <rect x="0" y="6" width="3" height="4" rx="0.6" />
                <rect x="4" y="4" width="3" height="6" rx="0.6" />
                <rect x="8" y="2" width="3" height="8" rx="0.6" />
                <rect x="12" y="0" width="3" height="10" rx="0.6" />
              </svg>
              <svg width="22" height="11" viewBox="0 0 22 11">
                <rect x="0.5" y="0.5" width="18" height="10" rx="2.5" fill="none" stroke="currentColor" opacity="0.5" />
                <rect x="2" y="2" width="12" height="7" rx="1.4" fill="currentColor" />
                <rect x="19.6" y="3.5" width="1.6" height="4" rx="0.8" fill="currentColor" opacity="0.5" />
              </svg>
            </span>
          </div>

          {/* Topp og faner, som i appen */}
          <div className="flex items-center gap-2.5 px-4 pt-2">
            <BrandMark size={30} />
            <div className="leading-tight">
              <div className="font-extrabold text-[15px]">Racecloud</div>
              <div className="text-[9.5px] font-bold" style={{ color: '#10b981' }}>
                ● Regatta: Onsdagsregatta
              </div>
            </div>
          </div>
          <div className="flex gap-0.5 px-3 mt-3 text-[10.5px] font-semibold" style={{ color: APP.svak }}>
            {['Båter', 'Seilaser', 'Instrument', 'Start'].map((t) => (
              <span
                key={t}
                className="px-2 py-1 rounded-md"
                style={t === 'Start' ? { background: APP.fane, color: '#fff' } : undefined}
              >
                {t}
              </span>
            ))}
          </div>

          <div className="px-3 pt-2 pb-3 space-y-2">
            {/* Hva klokka er knyttet til */}
            <div
              className="relative flex items-center rounded-lg border pl-3.5 pr-2.5 py-2 overflow-hidden"
              style={{ background: APP.kort, borderColor: APP.kant }}
            >
              <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: SEA }} />
              <div className="leading-tight">
                <div className="text-[8.5px] font-extrabold tracking-[0.12em]" style={{ color: SEA }}>
                  🏁 REGATTA
                </div>
                <div className="text-[13px] font-bold mt-0.5">Onsdagsregatta</div>
                <div className="text-[9.5px] mt-0.5" style={{ color: APP.dempet }}>
                  GJ77 Måken · i dag 18:30
                </div>
              </div>
              <span className="ml-auto rounded-md px-2.5 py-1.5 text-[10px] font-bold" style={{ background: SEA }}>
                Bytt
              </span>
            </div>

            {/* Startklokka i ett-minutt-fasen */}
            <div className="rounded-xl border px-3 py-3 text-center" style={{ background: '#6b3a0b', borderColor: APP.kant }}>
              <div className="text-[9.5px] font-semibold" style={{ color: APP.dempet }}>
                Fast starttid i dag 18:30 · synkronisert
              </div>
              <div className="text-[46px] leading-none font-extrabold tabular-nums mt-1.5">00:48</div>
              <div className="text-[11px] font-semibold mt-1.5" style={{ color: '#dceaef' }}>
                Ett minutt — P ned
              </div>
              <div className="flex justify-center gap-5 mt-2.5">
                <div className="flex flex-col items-center gap-1">
                  <FlagGlyph code="G" className="w-11 h-[30px]" />
                  <span className="text-[8px] font-bold uppercase tracking-wide text-yellow-300">Klasseflagg</span>
                </div>
                <div className="flex flex-col items-center gap-1 opacity-40">
                  <FlagGlyph code="P" className="w-11 h-[30px]" />
                  <span className="text-[8px] font-bold uppercase tracking-wide text-slate-400">Blue Peter</span>
                </div>
              </div>
              <div className="text-[9.5px] mt-2" style={{ color: APP.dempet }}>
                🔔 Lyd på · vibrasjon på
              </div>
            </div>

            {/* Startlinje-avlesninger */}
            <div className="grid grid-cols-2 gap-2">
              {avlesninger.map((a) => (
                <div
                  key={a.label}
                  className="rounded-lg border px-2.5 py-2"
                  style={{ background: APP.kort, borderColor: APP.kant }}
                >
                  <div className="text-[9.5px]" style={{ color: APP.dempet }}>
                    {a.label}
                  </div>
                  <div className={`font-extrabold leading-tight mt-0.5 ${a.stor ? 'text-[22px]' : 'text-[17px]'}`}>
                    {a.verdi}
                  </div>
                  {a.hint && (
                    <div className="text-[8.5px] mt-0.5" style={{ color: APP.svak }}>
                      {a.hint}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="h-1 w-24 mx-auto mb-2 rounded-full bg-white/40" />
        </div>
      </div>
      <figcaption className="mt-4 text-center text-xs text-slate-500">
        Startklokka i appen: 48 sekunder til start — og 17 sekunder å brenne.
      </figcaption>
    </figure>
  );
}

/** Appen om bord: telefonen er båtens software. */
function AppSection() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-14 border-t border-slate-800/70">
      <div className="grid gap-x-12 gap-y-9 lg:grid-cols-[minmax(0,1fr)_272px]">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] uppercase" style={{ color: SAND }}>
            Appen om bord
          </p>
          <h2 className="mt-3 font-extrabold tracking-tight text-white text-2xl sm:text-3xl leading-tight">
            Telefonen og appen er båtens software
          </h2>
          <p className="mt-4 text-slate-300 max-w-[62ch]">
            Telefonen du allerede har i lomma, blir båtens startklokke, startlinje-instrument, fartsmåler og
            GPS-sporer. Logg inn med samme konto som her, og velg regattaen båten er meldt på — eller start en
            trening. Appen viser hele tiden hva den logger.
          </p>
        </div>
        <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center">
          <PhoneMock />
        </div>
        <div>
          <div className="grid gap-4 sm:grid-cols-2">
            {APP_FUNKSJONER.map((f) => (
              <div key={f.tittel} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <span
                    className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                    style={{ background: 'rgba(23,162,184,.14)', color: SEA }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {f.ikon}
                    </svg>
                  </span>
                  <h3 className="font-semibold text-white">{f.tittel}</h3>
                </div>
                <p className="text-sm text-slate-400 mt-2.5 leading-relaxed">{f.tekst}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-5">
            Appen kommer for iPhone og Android. Uten app kan du fortsatt følge startklokka og live-kartet i
            nettleseren.
          </p>
        </div>
      </div>
    </section>
  );
}

/** Kompakt «tre roller»-stripe for nye besøkende. */
function RolesStrip() {
  const roles = [
    {
      bar: SEA,
      tag: 'Standard',
      name: 'Seiler',
      text: 'Registrer egne båter, meld på regattaer — også i andre klubber — og følg løp live.',
    },
    {
      bar: SAND,
      tag: 'Egen klubb',
      name: 'Klubbadmin',
      text: 'Klubbens båter, bøyer, baner og regattaer. Start løp, bekreft målgang, resultatlister.',
    },
    {
      bar: '#9fb8cb',
      tag: 'På tvers',
      name: 'Superadmin',
      text: 'Opprett klubber, utnevn klubbadmin, administrer trackere og hele plattformen.',
    },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 py-12 border-t border-slate-800/70">
      <h2 className="text-xs font-bold tracking-[0.14em] uppercase" style={{ color: SAND }}>
        Én innlogging — tre roller
      </h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {roles.map((r) => (
          <div
            key={r.name}
            className="relative bg-slate-900/50 border border-slate-800 rounded-xl p-5 overflow-hidden"
          >
            <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: r.bar }} />
            <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-slate-500">{r.tag}</div>
            <div className="font-semibold text-lg text-white mt-0.5">{r.name}</div>
            <p className="text-sm text-slate-400 mt-2">{r.text}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-500 mt-5">
        Ny her?{' '}
        <Link to="/konto/ny" className="text-sky-400 hover:text-sky-300 font-semibold">
          Be om en konto
        </Link>{' '}
        — så godkjennes du og kan melde på båtene dine.
      </p>
    </section>
  );
}

export function HomePage() {
  return (
    <SiteLayout>
      <Hero />
      <AppSection />
      <RolesStrip />
    </SiteLayout>
  );
}
